import { TablePaginationProps, styled, Box, Typography } from '@mui/material';
import {
  DataGrid,
  DataGridProps,
  GridPagination,
  gridFilteredTopLevelRowCountSelector,
  gridPageSizeSelector,
  useGridApiContext,
  useGridRootProps,
  useGridSelector,
} from '@mui/x-data-grid';
import React from 'react';
import MuiPagination from '@mui/material/Pagination';

type TableProps = DataGridProps & {
  tableCss?: React.CSSProperties; // Added extra props type here
  showSelectedRowText?: React.ReactNode;
  paginationContent?: () => JSX.Element;
  noResultText?: string;
  noRowText?: string;
  handlePagination?: (data: any) => void;
  currentPage?: number;
  isLoading?: boolean
};

const StyledDataGrid = styled(DataGrid)(() => ({
  border: 'none',


  '& .MuiDataGrid-columnHeaders': {
    backgroundColor: 'var(--neutral-200)',
    borderRadius: '4px 4px 0 0',
    borderTop: '1px solid var(--neutral-500)',
    borderLeft: '1px solid var(--neutral-500)',
    borderRight: '1px solid var(--neutral-500)',
    '&::after': {
      display: 'none !important',
    },
    '&:hover': {
      border: 'none',
    },
  },

  '& .MuiDataGrid-virtualScrollerContent': {
    marginBottom: '14px !important',
  },

  '& .MuiDataGrid-virtualScrollerRenderZone': {},
  '& .MuiDataGrid-columnSeparator': {
    display: 'none',
  },

  '& .MuiDataGrid-filler': {
    display: 'none',
  },

  '& .MuiDataGrid-row': {
    borderLeft: '1px solid var(--neutral-500)',
    borderRight: '1px solid var(--neutral-500)',
    borderBottom: '1px solid var(--neutral-500)',
    '&:hover': {
      background: 'var(--blue-20)',
    },
    '&.Mui-selected': {
      backgroundColor: 'var(--purple-25)',
      color: 'var(--gray-700)',
    },
    color: 'var(--gray-25)',
  },

  '& .MuiDataGrid-cell': {
    border: 'none',
    color: 'inherit',
    '&.Muidatagrid-cell--editing': {
      backgroundColor: 'var(--base-white)',
    },
    '&:focus': {
      outline: 'none',
    },
    '&.MuiDataGrid-cell--withRenderer.Mui-disabled': {
      // Style for the cell containing the disabled checkbox
      backgroundColor: 'var(--base-white)', // Adjust the color to match your design
      opacity: 0.5, // Adjust the opacity to get the desired disabled look
    },
  },

  '& .MuiDataGrid-row.MuiDataGrid-row--editing': {
    backgroundColor: 'inherit',
  },

  '& .row-disabled': {
    backgroundColor: 'var(--neutral-100)',
    '& .MuiDataGrid-cell': {
      color: 'var(--gray-25)',
    },
  },

  '& .MuiSvgIcon-root.Mui-disabled': {
    // Style for the SVG icon inside the disabled checkbox
    fill: 'var(--neutral-900)', // Change this to the color you want for the checkbox icon
  },

  '.MuiDataGrid-overlay': {
    justifyContent: 'flex-start',
    padding: '0.5rem',
  },

  '& .MuiDataGrid-footerContainer': {
    display: 'flex',
    backgroundColor: 'var(--base-white)',
    border: 'none',
    outline: 'none',
    '& .MuiTablePagination-root': {
      '& .MuiToolbar-root': {
        padding: 'unset',
      },
    },
    '> div:first-of-type': {
      display: 'none',
    },
  },
}));

const getPageCount = (rowCount: number, pageSize: number): number => {
  if (pageSize > 0 && rowCount > 0) {
    return Math.ceil(rowCount / pageSize);
  }
  return 0;
};

function Pagination({
  onPageChange,
  className,
  handlePagination,
  currentPage,
}: Pick<TablePaginationProps, 'page' | 'onPageChange' | 'className'> & {
  handlePagination?: (page: number) => void; // Add type for handlePagination
} & { currentPage?: number }) {
  const apiRef = useGridApiContext();
  const rootProps = useGridRootProps();
  const pageSize = useGridSelector(apiRef, gridPageSizeSelector);
  const visibleTopLevelRowCount = useGridSelector(
    apiRef,
    gridFilteredTopLevelRowCountSelector,
  );

  const pageCount = getPageCount(
    rootProps.rowCount ?? visibleTopLevelRowCount,
    pageSize,
  );

  return (
    <MuiPagination
      color="primary"
      className={className}
      count={pageCount}
      page={currentPage}
      boundaryCount={2}
      onChange={(event, newPage) => {
        onPageChange(event as any, newPage - 1);
        if (handlePagination) {
          handlePagination(newPage); // Call handlePagination with the new page number
        }
      }}
      sx={{
        '& .MuiPaginationItem-root': {
          '&.Mui-selected': {
            color: 'var(--blue-500)',
            backgroundColor: 'transparent',
          },
        },

        '&.MuiPagination-root': {
          flexWrap: 'nowrap',
          marginLeft: 'unset',
        },
      }}
    />
  );
}

function CustomPagination(
  total: number,
  paginationContent?: () => JSX.Element,
  handlePagination?: (data: any) => void,
  currentPage?: number,
) {
  return (
    <Box
      display="flex"
      justifyContent="space-between"
      alignItems="center"
      width="100%"
    >
      <GridPagination
        sx={{
          display: 'flex'
        }}
        ActionsComponent={(props) => (
          <Pagination
            {...props}
            handlePagination={handlePagination}
            currentPage={currentPage}
          />
        )}
        labelDisplayedRows={() => null}
      />
      <Box display="flex" gap={1}>
        <Typography variant="body2" fontWeight={500} color="var(--neutral-900)">
          조회 결과 : {total}
        </Typography>
        {paginationContent && paginationContent()}
      </Box>
    </Box>
  );
}

const Table: React.FC<TableProps> = ({
  checkboxSelection = false,
  isLoading,
  disableRowSelectionOnClick = true,
  autoPageSize = false,
  initialState = {
    pagination: { paginationModel: { pageSize: 10 } },
  },
  slots,
  showSelectedRowText,
  paginationContent,
  noResultText,
  noRowText,
  handlePagination,
  currentPage,
  ...props
}) => {
  return (
    <Box>
      <StyledDataGrid
        paginationMode="server"
        localeText={{
          noRowsLabel: noResultText ? noResultText : `${'No Data'}`,
          noResultsOverlayLabel: noResultText ? noResultText : `${'No Data'}`,
        }}
        checkboxSelection={checkboxSelection}
        disableRowSelectionOnClick={disableRowSelectionOnClick}
        autoPageSize={autoPageSize}
        initialState={initialState}
        hideFooterSelectedRowCount
        pageSizeOptions={[10]}
        rowHeight={48}
        columnHeaderHeight={48}
        sx={{
          minHeight: isLoading === undefined ? 0 : 300
        }}
        loading={isLoading}
        slotProps={{
          loadingOverlay: {
            variant: 'skeleton',
            noRowsVariant: 'skeleton'
          }
        }}
        slots={{
          ...slots,
          noRowsOverlay: isLoading === undefined ? undefined : () => (
            <Box display={'flex'} justifyContent={'center'} alignItems={'center'} height={'100%'}
              sx={{
                backgroundColor: '#f5f6f7'
              }}
            >
              <Typography>
                {noResultText ? noResultText : `${'No Data'}`}
              </Typography>
            </Box>
          ),
          pagination: () => {
            return CustomPagination(
              props?.rowCount ?? 0,
              paginationContent,
              handlePagination,
              currentPage,
            );
          },
        }}
        {...props}
      />
    </Box>
  );
};

export default Table;
